import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import { toast } from 'react-toastify';

const CUSTOMER_ROLES = ['customer', 'affiliate'];

const LoginPage = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaMethod, setMfaMethod] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectAfterLogin = (result) => {
    if (result?.user) {
      const role = result.user.role;
      if (CUSTOMER_ROLES.includes(role)) {
        const from = location.state?.from?.pathname || (role === 'affiliate' ? '/affiliate/dashboard' : '/');
        navigate(from, { replace: true });
      } else {
        toast.info('Redirecting to your portal...');
        if (['farmer', 'farmer_staff'].includes(role)) navigate('/merchant/dashboard');
        else navigate('/choose-role');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result?.mfaRequired) {
        // MFA is enabled — show code input
        setMfaStep(true);
        setMfaMethod(result.mfaMethod);
        // Trigger MFA challenge (sends OTP for email/sms)
        try { await api.post('/mfa/challenge', { email: form.email }); } catch {}
        toast.info(result.mfaMethod === 'authenticator' ? 'Enter your authenticator code' : `Code sent via ${result.mfaMethod}`);
      } else {
        redirectAfterLogin(result);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    }
    setLoading(false);
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/mfa/verify-login', { email: form.email, code: mfaCode });
      if (data.token) {
        localStorage.setItem('farmdirectly_token', data.token);
        window.location.href = '/'; // Full reload to pick up auth
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'linear-gradient(135deg, #FEF2F2 0%, #FFF7ED 50%, #FFFBEB 100%)' }}>
      <div className="auth-sidebar" style={{
        flex: '0 0 420px', background: 'linear-gradient(180deg, #B91C1C 0%, #991B1B 60%, #7F1D1D 100%)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        padding: '60px 40px', color: '#FFF', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(234, 179, 8, 0.15)', pointerEvents: 'none' }} />
        <div style={{ fontSize: '36px', fontWeight: 800, fontFamily: "'Poppins', sans-serif", marginBottom: '8px' }}>
          eBuy<span style={{ color: '#EAB308' }}>Wiz</span>
        </div>
        <p style={{ opacity: 0.8, fontSize: '14px', marginBottom: '48px', fontStyle: 'italic' }}>{t('multiVendorMarketplace')}</p>
        <div style={{ fontSize: '72px', marginBottom: '20px' }}>🛍️</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '28px', fontWeight: 700, textAlign: 'center', marginBottom: '12px' }}>{t('welcomeBack')}</h2>
        <p style={{ textAlign: 'center', opacity: 0.75, fontSize: '14px', lineHeight: 1.7, maxWidth: '280px' }}>
          {t("signInSubtext")}
        </p>
        <div style={{ marginTop: '48px', display: 'flex', gap: '20px', opacity: 0.6, fontSize: '12px', fontWeight: 600 }}>
          <span>🛒 {t('cart')}</span><span>💝 {t('wishlist')}</span><span>🏆 {t('points')}</span><span>📦 {t('orders')}</span>
        </div>
      </div>

      <div className="auth-form-container" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: '440px' }}>
          <div className="d-lg-none" style={{ marginBottom: '16px' }}>
            <span style={{ fontSize: '28px', fontWeight: 800, color: '#B91C1C', fontFamily: "'Poppins', sans-serif" }}>eBuy<span style={{ color: '#EAB308' }}>Wiz</span></span>
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '28px', color: '#1F2937', marginBottom: '4px' }}>{t("customerSignIn")}</h1>
          <p style={{ color: '#B91C1C', fontWeight: 600, fontSize: '14px', marginBottom: '28px' }}>{t("shopperAndAffiliate")}</p>

          {location.state?.registered && <div style={{ background: '#F0FDF4', color: '#15803D', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px' }}>✅ Account created! Please sign in.</div>}
          {error && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}

          {!mfaStep ? (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>{t("emailAddress")}*</label>
                <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} placeholder="you@example.com" />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>{t("password")}*</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={{ ...inputStyle, paddingRight: '44px' }} placeholder={t("password")} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9CA3AF', fontSize: '18px' }} title={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div style={{ textAlign: 'right', marginBottom: '20px' }}>
                <Link to="/forgot-password" style={{ color: '#B91C1C', fontSize: '14px', fontWeight: 600, textDecoration: 'none' }}>{t("forgotPassword")}</Link>
              </div>
              <button type="submit" disabled={loading} style={{ ...btnStyle, background: '#B91C1C' }}>{loading ? '...' : t('signIn')}</button>
            </form>
          ) : (
            <form onSubmit={handleMfaVerify}>
              <div style={{ background: '#FEF3C7', padding: '14px', borderRadius: '10px', marginBottom: '20px', textAlign: 'center' }}>
                <p style={{ margin: 0, fontWeight: 600, color: '#92400E', fontSize: '14px' }}>
                  🔐 {mfaMethod === 'authenticator' ? 'Enter code from your authenticator app' : `Enter the code sent via ${mfaMethod}`}
                </p>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '6px' }}>Verification Code</label>
                <input type="text" required maxLength={8} value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/[^A-Za-z0-9]/g, ''))}
                  style={{ ...inputStyle, textAlign: 'center', letterSpacing: '6px', fontWeight: 700, fontFamily: 'monospace', fontSize: '20px' }}
                  placeholder="000000" autoFocus />
                <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '6px' }}>You can also use a backup code</p>
              </div>
              <button type="submit" disabled={loading || mfaCode.length < 6} style={{ ...btnStyle, background: '#B91C1C' }}>{loading ? 'Verifying...' : 'Verify & Sign In'}</button>
              <button type="button" onClick={() => { setMfaStep(false); setMfaCode(''); setError(''); }} style={{ width: '100%', padding: '12px', border: 'none', background: 'transparent', color: '#6B7280', fontWeight: 600, cursor: 'pointer', marginTop: '8px' }}>← Back to login</button>
            </form>
          )}

          <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#6B7280' }}>
            {t("dontHaveAccount")} <Link to="/register/customer" style={{ color: '#B91C1C', fontWeight: 600, textDecoration: 'none' }}>{t("createOne")} →</Link>
          </p>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <p style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '8px' }}>{t("merchantOrTeam")}</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <Link to="/merchant/login" style={{ fontSize: '12px', fontWeight: 600, color: '#059669', textDecoration: 'none', padding: '6px 16px', border: '1px solid #D1FAE5', borderRadius: '8px' }}>{t("merchantLogin")}</Link>
              <Link to="/admin/admin_login" style={{ fontSize: '12px', fontWeight: 600, color: '#7C3AED', textDecoration: 'none', padding: '6px 16px', border: '1px solid #EDE9FE', borderRadius: '8px' }}>🛡️ {t('operationsLogin')}</Link>
            </div>
          </div>
          <p style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#D1D5DB' }}>
            <Link to="/choose-role" style={{ color: '#D1D5DB', textDecoration: 'none' }}>{t('viewAllRoles')} →</Link>
          </p>
        </div>
      </div>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Playfair+Display:ital,wght@0,700;1,700&display=swap" rel="stylesheet" />
    </div>
  );
};

const inputStyle = { width: '100%', padding: '14px 16px', border: '2px solid #E5E7EB', borderRadius: '10px', fontSize: '15px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Poppins', sans-serif" };
const btnStyle = { width: '100%', padding: '16px', border: 'none', borderRadius: '10px', color: '#FFF', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: "'Poppins', sans-serif", letterSpacing: '0.5px' };

export default LoginPage;
